import * as React from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { PortalWrapper } from "../prim.portal-wrapper";
import { cn } from "../util.cn/cn";

const { Root } = DialogPrimitive;

const { Trigger } = DialogPrimitive;

const { Portal } = DialogPrimitive;

const Overlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      className={cn(
      `
        fixed inset-0 z-50 bg-overlay
        data-[state=closed]:animate-out
        data-[state=closed]:fade-out-0
        data-[state=open]:animate-in
        data-[state=open]:fade-in-0
      `,
      className,
      )}
      ref={ref}
      {...props}
    />
  );
});
Overlay.displayName = DialogPrimitive.Overlay.displayName;

const Close = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Close
      className={cn(
      `
        absolute right-4 top-4 rounded-2 opacity-70
        ring-offset-2 transition-opacity
        data-[state=open]:bg-panel
        data-[state=open]:text-gray-11
        disabled:pointer-events-none
        focus:outline-none focus:ring-2 focus:ring-gray-7
        focus:ring-offset-2
        hover:opacity-100
      `,
      className,
      )}
      ref={ref}
      {...props}
    >
      <XIcon className="size-4" />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  );
});
Close.displayName = DialogPrimitive.Close.displayName;

const CloseUnstyled = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, children, ...props }, ref) => {
  return (
    <DialogPrimitive.Close
      className={className}
      ref={ref}
      asChild
      {...props}
    >
      {children}
    </DialogPrimitive.Close>
  );
});
CloseUnstyled.displayName = "DialogPrimitive.CloseUnstyled";

const Content = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ children, className, ...props }, ref) => {
  return (
    <Portal>
      <PortalWrapper>
        <Overlay />
        <DialogPrimitive.Content
          className={cn(
        `
          fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg
          -translate-x-1/2 -translate-y-1/2 gap-4 border
          border-gray-6 bg-panel p-6 shadow-3 duration-200
          data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0
          data-[state=closed]:zoom-out-95
          data-[state=closed]:slide-out-to-left-1/2
          data-[state=closed]:slide-out-to-top-[48%]
          data-[state=open]:animate-in
          data-[state=open]:fade-in-0
          data-[state=open]:zoom-in-95
          data-[state=open]:slide-in-from-left-1/2
          data-[state=open]:slide-in-from-top-[48%]
          sm:rounded-4
        `,
        className,
          )}
          ref={ref}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </PortalWrapper>
    </Portal>
  );
});
Content.displayName = DialogPrimitive.Content.displayName;

const ContentMinStyled = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ children, className, ...props }, ref) => {
  return (
    <Portal>
      <PortalWrapper>
        <Overlay />
        <DialogPrimitive.Content
          className={cn(
            `
              fixed left-1/2 top-1/2 z-50 grid w-full
              max-w-lg -translate-x-1/2 -translate-y-1/2
              gap-4 duration-200
              data-[state=closed]:animate-out
              data-[state=closed]:fade-out-0
              data-[state=closed]:zoom-out-95
              data-[state=closed]:slide-out-to-left-1/2
              data-[state=closed]:slide-out-to-top-[48%]
              data-[state=open]:animate-in
              data-[state=open]:fade-in-0
              data-[state=open]:zoom-in-95
              data-[state=open]:slide-in-from-left-1/2
              data-[state=open]:slide-in-from-top-[48%]
              sm:rounded-4
            `,
            className,
          )}
          ref={ref}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </PortalWrapper>
    </Portal>
  );
});
ContentMinStyled.displayName = `${DialogPrimitive.Content.displayName}-ContentMinStyled`;

const ContentUnStyled = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ children, className, ...props }, ref) => {
  return (
    <Portal>
      <PortalWrapper>
        <Overlay />
        <DialogPrimitive.Content
          className={cn(
            className,
          )}
          ref={ref}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </PortalWrapper>
    </Portal>
  );
});
ContentUnStyled.displayName = `${DialogPrimitive.Content.displayName}-ContentUnStyled`;

const Header = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
      `
        flex flex-col space-y-1.5 text-center
        sm:text-left
      `,
      className,
      )}
      {...props}
    />
  );
};
Header.displayName = "DialogHeader";

const Footer = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
      `
        flex flex-col-reverse
        sm:flex-row sm:justify-end sm:space-x-2
      `,
      className,
      )}
      {...props}
    />
  );
};
Footer.displayName = "DialogFooter";

const Title = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Title
      className={cn(
        "text-4 font-medium",
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
Title.displayName = DialogPrimitive.Title.displayName;

const Description = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Description
      className={cn(`text-2 text-gray-11`, className)}
      ref={ref}
      {...props}
    />
  );
});
Description.displayName = DialogPrimitive.Description.displayName;

export {
  Close,
  CloseUnstyled,
  Content,
  ContentMinStyled,
  ContentUnStyled,
  Description,
  Footer,
  Header,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger,
};
